import React, { useMemo } from 'react';
import { Grid, Button, makeStyles, styled } from '@material-ui/core';
import { Email, BookmarkBorderOutlined, Bookmark } from '@material-ui/icons';
import { useIntl } from 'react-intl';
import { noop } from 'utils/noop';

import { SdmRichText, SdmStack } from '..';
import translations from 'utils/translations';

import { Contents, Row, Container, Divider } from './Contents';
import { PageGeneratorThemeProvider, Typography, theme } from './mui-overrides';

const useStyles = makeStyles(() => ({
  actions: {
    marginBottom: 20,
  },
  overview: {
    ...theme.typography.overview,
    '& div[data-contents=true] > div[data-block=true] > div[data-offset-key]': {
      padding: 0,
      marginBottom: 10,
    },
    '& div[data-contents=true] > div[data-block=true]:last-of-type > div[data-offset-key]':
      {
        marginBottom: 0,
      },
  },
  cover: {
    ['@media (min-width: 992px)']: {
      marginBottom: 70,
    },
    marginBottom: 40,
  },
}));

const Image = styled('img')({
  width: '100%',
});

export const BrandPageTemplate = ({
  name,
  cover,
  overview,
  contents = [],
  onContactUs = noop,
  onBookmarks = noop,
  bookmarks,
  socialLinks,
}) => {
  const intl = useIntl();
  const i18n = translations.brandPage(intl);

  const classes = useStyles();

  const hasSocialLinks = socialLinks?.length > 0;

  const TwitterIcon =
    '/themes/custom/sdm/images/footer/social-icons/Twitter-red.png';
  const FacebookIcon =
    '/themes/custom/sdm/images/footer/social-icons/Facebook-red.png';
  const InstagramIcon =
    '/themes/custom/sdm/images/footer/social-icons/Instagram-red.png';
  const LinkedinIcon =
    '/themes/custom/sdm/images/footer/social-icons/Linkedin-red.png';
  const PinterestIcon =
    '/themes/custom/sdm/images/footer/social-icons/Pinterest-red.png';

  const SocialIconsMapping = {
    facebook: FacebookIcon,
    instagram: InstagramIcon,
    linkedin: LinkedinIcon,
    pinterest: PinterestIcon,
    twitter: TwitterIcon,
  };

  return (
    <PageGeneratorThemeProvider>
      <Container>
        <SdmStack>
          <Typography variant="subtitle">{i18n.brandPage}</Typography>
          <Typography variant="title">{name}</Typography>
          <Grid item xs={12} className={classes.actions} container>
            <Grid container item md xs={12} justify="center">
              <Button
                color="primary"
                variant="text"
                onClick={() => onContactUs()}
                startIcon={<Email />}
              >
                {i18n.contactUs}
              </Button>
            </Grid>
            <Grid container item md xs={12} justify="center">
              <Button
                color="primary"
                variant="text"
                startIcon={
                  bookmarks ? <Bookmark /> : <BookmarkBorderOutlined />
                }
                onClick={() => onBookmarks()}
              >
                {bookmarks ? i18n.removeFromBookmarks : i18n.addToBookmarks}
              </Button>
            </Grid>
            {hasSocialLinks ? (
              <Grid container item md xs={12} justify="center">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 5,
                  }}
                >
                  {socialLinks.map((entity) =>
                    SocialIconsMapping[entity.social] ? (
                      <img
                        key={entity.social}
                        src={SocialIconsMapping[entity.social]}
                        alt="facebook_icon"
                        style={{ width: 30, cursor: 'pointer' }}
                      />
                    ) : null
                  )}
                </div>
              </Grid>
            ) : null}
          </Grid>
          <Image className={classes.cover} src={cover.img} alt={cover.alt} />
          <Row>
            <SdmRichText
              editorClassName={classes.overview}
              variant="white"
              editorStyleOverride={{
                maxHeight: 'none',
                marginRight: 0,
                paddingLeft: 0,
                minHeight: 'none !important',
              }}
              formControlStyle={{
                margin: 0,
              }}
              labelStyle={{
                display: 'none',
              }}
              value={overview}
              readOnly
            />
          </Row>
          <Row>
            <Divider />
          </Row>
        </SdmStack>
      </Container>
      <SdmStack>
        <Contents contents={contents} />
      </SdmStack>
    </PageGeneratorThemeProvider>
  );
};

export function useBrandDefaultValue() {
  return useMemo(() => {
    return {
      name: 'Brand',
      cover: {
        img: 'https://via.placeholder.com/1200x900',
        alt: '1200x900',
      },
      overview: {
        blocks: [
          {
            key: (Math.random() * 10000).toFixed(0),
            text: '',
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {},
          },
        ],
        entityMap: {},
      },
    };
  }, []);
}
